import { packTotalCents, type ListedPiece } from "./catalog-types";
import { reaisToCents } from "./money";

/**
 * Price range histogram (handoff §7.3).
 *
 * The invariant that drives every rule here: **no bar may offer an empty range**, because
 * an empty bar is a target that leads to "no results" in the very control that exists to
 * show where the pieces are. Hence a floor taken from the ladder below the cheapest total,
 * intermediate cuts halved down to five, and empty buckets merged into a neighbour.
 */

/** Ladder of cut points, in reais, literal from the prototype. */
const LADDER_REAIS = [150, 300, 600, 1000, 1500, 2500, 5000, 10000, 25000, 50000] as const;

const MAX_INTERMEDIATE_CUTS = 5;
const MAX_MERGE_PASSES = 12;

export type PriceHistogram = {
  /** Boundaries in cents, N+1 values. */
  buckets: number[];
  /** Count per bucket, N values. */
  counts: number[];
  /** Axis labels, with "k" above a thousand. */
  labels: string[];
  /** Top of the range, in cents. */
  maxCents: number;
};

/** "1,5k" above a thousand, the bare number below. Prototype rule. */
export function histogramLabel(cents: number): string {
  const reais = cents / 100;

  if (reais >= 1000) {
    return `${(Math.round(reais / 100) / 10).toLocaleString("pt-BR")}k`;
  }

  return String(Math.round(reais));
}

/**
 * Top boundary, guaranteed to sit above the last boundary below it.
 *
 * Covers the degenerate scope where every piece costs exactly one ladder value: floor and
 * highest coincide, and the boundaries would come out zero-width, breaking the increasing
 * invariant. The borrowed width is the next ladder step, doubling past the last one.
 */
function topBoundary(
  ladder: readonly number[],
  lastBoundary: number,
  highest: number,
): number {
  if (highest > lastBoundary) return highest;

  return ladder.find((value) => value > lastBoundary) ?? lastBoundary * 2;
}

function countBuckets(totals: readonly number[], buckets: readonly number[]): number[] {
  return buckets.slice(0, -1).map((low, index) => {
    const high = buckets[index + 1];
    const isLast = index === buckets.length - 2;

    return totals.filter((total) => total >= low && (isLast ? total <= high : total < high)).length;
  });
}

/**
 * Returns `null` when the scope carries no price, in which case the whole facet disappears
 * rather than offering a single-bar histogram.
 */
export function priceHistogram(pieces: readonly ListedPiece[]): PriceHistogram | null {
  return histogramOf(pieces.map((piece) => packTotalCents(piece)).filter((total) => total > 0));
}

/**
 * The same histogram over the served catalogue (filters spec §5).
 *
 * The **bounds** are the catalogue's own, as the server counted them, and in the unit the server
 * filters by — the product's price, not the pack's total. Anything else closes the slider in on
 * itself page after page, and makes the bucket a person aims at exclude the very piece that put
 * it there. The **bars** are the page on screen: texture for choosing, never the filter itself.
 */
export function serverPriceHistogram(
  pieces: readonly ListedPiece[],
  facets: { price: { minCents: number; maxCents: number } | null },
): PriceHistogram | null {
  if (facets.price === null) return null;

  const onScreen = pieces.map((piece) => piece.priceCents).filter((price) => price > 0);

  return histogramOf([facets.price.minCents, ...onScreen, facets.price.maxCents].filter((price) => price > 0));
}

function histogramOf(totals: readonly number[]): PriceHistogram | null {
  if (totals.length === 0) return null;

  const highest = Math.max(...totals);
  const lowest = Math.min(...totals);

  const ladder = LADDER_REAIS.map(reaisToCents);
  const floor = ladder.filter((value) => value <= lowest).pop() ?? 0;

  let cuts = ladder.filter((value) => value > floor && value < highest);
  while (cuts.length > MAX_INTERMEDIATE_CUTS) {
    cuts = cuts.filter((_, index) => index % 2 === 0);
  }

  // With any cut present the highest total exceeds the last one, since cuts are filtered
  // by `< highest`. So `topBoundary` only changes anything in the degenerate case.
  let buckets = [floor, ...cuts, topBoundary(ladder, cuts.at(-1) ?? floor, Math.ceil(highest))];

  for (let pass = 0; pass < MAX_MERGE_PASSES && buckets.length > 2; pass += 1) {
    const emptyIndex = countBuckets(totals, buckets).indexOf(0);
    if (emptyIndex === -1) break;

    // An empty bucket at the end merges backwards; anywhere else, forwards.
    const cutIndex = emptyIndex === buckets.length - 2 ? emptyIndex : emptyIndex + 1;
    buckets = buckets.filter((_, index) => index !== cutIndex);
  }

  return {
    buckets,
    counts: countBuckets(totals, buckets),
    labels: buckets.map(histogramLabel),
    maxCents: buckets[buckets.length - 1],
  };
}
